import { useMemo } from 'react'

import { type Edge } from '@xyflow/react'

import {
  buildNodeMap,
  buildParentMap,
} from 'modules/job_catalog/components/JobCatalogTree/treeUtils'
import { type CustomNode } from 'modules/job_catalog/components/JobCatalogTree/types'
import { type NodeType, normalizeNodeType } from 'modules/job_catalog/lib/nodesUtils'

export interface SearchNodeResult {
  ancestorName?: string
  id: string
  displayName: string
  levelName?: string
  name: string
  type: NodeType
}

const normalizeSearchValue = (value: string) => value.trim().toLowerCase()

const matchesNode = (node: CustomNode, search: string) =>
  node.data.name.toLowerCase().includes(search)

const getMatchScore = (node: CustomNode, search: string) => {
  const normalizedName = node.data.name.toLowerCase()

  if (normalizedName === search) {
    return 0
  }

  if (normalizedName.startsWith(search)) {
    return 1
  }

  return 2
}

const sortResults = (left: CustomNode, right: CustomNode, search: string) => {
  const leftScore = getMatchScore(left, search)
  const rightScore = getMatchScore(right, search)

  if (leftScore !== rightScore) {
    return leftScore - rightScore
  }

  const leftName = left.data.name.toLowerCase()
  const rightName = right.data.name.toLowerCase()

  return leftName.localeCompare(rightName)
}

const getLevelDisplayParts = (
  node: CustomNode,
  parentMap: Map<string, string>,
  nodeMap: Map<string, CustomNode>
) => {
  const parentNodeId = parentMap.get(node.id)
  const parentNode = parentNodeId ? nodeMap.get(parentNodeId) : undefined

  if (!parentNode) {
    return undefined
  }

  return {
    ancestorName: parentNode.data.name,
    levelName: node.data.name,
  }
}

const useSearchNodes = (nodes: CustomNode[], edges: Edge[], search: string) => {
  const normalizedSearch = normalizeSearchValue(search)

  const results = useMemo<SearchNodeResult[]>(() => {
    if (!normalizedSearch) {
      return []
    }

    const nodeMap = buildNodeMap(nodes)
    const parentMap = buildParentMap(edges)

    return nodes
      .filter((node) => matchesNode(node, normalizedSearch))
      .sort((left, right) => sortResults(left, right, normalizedSearch))
      .map((node) => {
        const nodeType = normalizeNodeType(node.data.type)
        const levelDisplayParts =
          nodeType === 'level' ? getLevelDisplayParts(node, parentMap, nodeMap) : undefined

        return {
          ancestorName: levelDisplayParts?.ancestorName,
          id: node.id,
          displayName: levelDisplayParts
            ? `${levelDisplayParts.ancestorName} / ${levelDisplayParts.levelName}`
            : node.data.name,
          levelName: levelDisplayParts?.levelName,
          name: node.data.name,
          type: nodeType,
        }
      })
  }, [edges, nodes, normalizedSearch])

  return {
    results,
    hasSearch: normalizedSearch.length > 0,
  }
}

export default useSearchNodes
